import { useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import PhoneInput from "react-phone-number-input/input";
import "react-phone-number-input/style.css";
import { PriceSummary } from "@/modules/eventRegistration/features/EventRegistration/components/PriceSummary/PriceSummary";
import { useRegistration } from "@/modules/eventRegistration/features/EventRegistration/context/RegistrationContext";
import { useNotification } from "@/context/NotificationContext";
import AddAttendees from "@/modules/eventRegistration/features/EventRegistration/components/AddAttendees/AddAttendees";
import {
  computeWaitlistedCount,
  computeConfirmedCount,
  recomputeAttendeeWaitlistFlags,
} from "@/modules/eventRegistration/features/EventRegistration/utils/attendeeWaitlist";
import { normalizeEmail } from "@/modules/eventRegistration/features/EventRegistration/utils/normalizeEmail";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ReviewStep() {
  const { showNotification } = useNotification();
  const {
    contactName,
    contactEmail,
    contactPhone,
    attendees,
    groupNotes,
    eventInfo,
    updateContact,
    setAttendees,
    nextStep,
  } = useRegistration();

  const eventPrice = eventInfo?.eventPrice ?? 0;
  const availableSeats = eventInfo?.availableSeats ?? 0;
  const isCoveredByTotalAccess = eventInfo?.isCoveredByTotalAccess ?? false;

  const confirmedCount = computeConfirmedCount(
    attendees.length,
    availableSeats,
  );
  const waitlistedCount = computeWaitlistedCount(
    attendees.length,
    availableSeats,
  );

  const [duplicateEmailError, setDuplicateEmailError] = useState<string | null>(
    null,
  );

  // Add a single attendee straight into context — no edit/save step. Adds
  // are deduped on email and the whole list's waitlist flags are recomputed
  // so the badges + price summary stay in sync immediately.
  function handleAddAttendee(attendee: {
    firstName: string;
    lastName: string;
    email?: string;
  }) {
    if (attendee.email) {
      const normalizedEmail = normalizeEmail(attendee.email);
      const isDuplicate = attendees.some(
        (a) => a.email && normalizeEmail(a.email) === normalizedEmail,
      );
      if (isDuplicate) {
        setDuplicateEmailError(
          `An attendee with email ${attendee.email} has already been added.`,
        );
        return;
      }
    }
    setDuplicateEmailError(null);
    setAttendees(
      recomputeAttendeeWaitlistFlags(
        [
          ...attendees,
          {
            firstName: attendee.firstName,
            lastName: attendee.lastName,
            email: attendee.email,
            isSelected: true,
            isWaitlist: false,
            specialRequests: undefined,
          },
        ],
        availableSeats,
      ),
    );
  }

  function handleRemoveAttendee(index: number) {
    setAttendees(
      recomputeAttendeeWaitlistFlags(
        attendees.filter((_, i) => i !== index),
        availableSeats,
      ),
    );
  }

  function handleSpecialRequestChange(index: number, value: string) {
    setAttendees(
      attendees.map((a, i) =>
        i === index ? { ...a, specialRequests: value } : a,
      ),
    );
  }

  function handleContinue() {
    if (!contactName || !contactEmail || !contactPhone) {
      showNotification(
        "Missing Contact Info",
        "Please fill in contact name, email, and phone.",
        "error",
      );
      return;
    }

    if (!EMAIL_PATTERN.test(contactEmail)) {
      showNotification(
        "Invalid Email",
        "Please enter a valid contact email address.",
        "error",
      );
      return;
    }

    if (attendees.length === 0) {
      showNotification(
        "No Attendees",
        "Please add at least one attendee.",
        "error",
      );
      return;
    }

    // Keep waitlist flags authoritative before advancing — buildEventCart
    // reads a.isWaitlist to decide who needs a paid seat.
    setAttendees(recomputeAttendeeWaitlistFlags(attendees, availableSeats));
    nextStep();
  }

  return (
    <section className="space-y-6">
      <h2 className="text-lg font-normal text-gray-900 dark:text-white">
        Register your team
      </h2>

      {/* Contact card — always editable, no save step. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contact Person</CardTitle>
          <CardDescription>
            This person will receive all event communications.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label
                className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                htmlFor="contact-name"
              >
                Name
              </Label>
              <Input
                id="contact-name"
                onChange={(e) => updateContact({ contactName: e.target.value })}
                placeholder="Full name"
                value={contactName}
              />
            </div>
            <div className="space-y-1.5">
              <Label
                className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                htmlFor="contact-email"
              >
                Email
              </Label>
              <Input
                id="contact-email"
                onChange={(e) =>
                  updateContact({ contactEmail: e.target.value })
                }
                placeholder="email@company.com"
                type="email"
                value={contactEmail}
              />
            </div>
            <div className="space-y-1.5">
              <Label
                className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                htmlFor="contact-phone"
              >
                Phone
              </Label>
              <PhoneInput
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                country="US"
                id="contact-phone"
                onChange={(value) =>
                  updateContact({ contactPhone: value || "" })
                }
                placeholder="(123) 456-7890"
                value={contactPhone}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attendees card — search or type, hit Add. No edit/save step. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Attendees
            <span className="text-sm font-normal text-muted-foreground ml-2">
              {attendees.length} total
              {waitlistedCount > 0 ? ` · ${waitlistedCount} waitlisted` : ""}
            </span>
          </CardTitle>
          <CardDescription>
            Search your team or add attendees by hand — each one is added the
            moment you hit Add.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <AddAttendees
            duplicateEmailError={duplicateEmailError}
            onAddAttendee={handleAddAttendee}
            onClearDuplicateEmailError={() => setDuplicateEmailError(null)}
          />

          {attendees.length > 0 && (
            <div className="border-t border-border pt-1">
              {attendees.map((a, index) => {
                const requestValue = a.specialRequests || "";
                const isWaitlisted = index >= availableSeats;
                return (
                  <div
                    className="py-3 border-b border-border last:border-b-0"
                    key={`${a.email ?? "noemail"}-${index}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {a.firstName} {a.lastName}
                      </span>
                      {a.email && (
                        <span className="text-xs text-muted-foreground">
                          {a.email}
                        </span>
                      )}
                      <span
                        className={`text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          isWaitlisted
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                            : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                        }`}
                      >
                        {isWaitlisted ? "Waitlist" : "Confirmed"}
                      </span>
                      <button
                        aria-label="Remove attendee"
                        className="ml-auto p-1 rounded text-muted-foreground/40 hover:text-destructive transition-colors flex-shrink-0"
                        onClick={() => handleRemoveAttendee(index)}
                        type="button"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="mt-2 space-y-1.5">
                      <Label
                        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                        htmlFor={`special-requests-${index}`}
                      >
                        Special requests
                      </Label>
                      <Textarea
                        className="resize-none"
                        id={`special-requests-${index}`}
                        onChange={(e) =>
                          handleSpecialRequestChange(index, e.target.value)
                        }
                        placeholder="Dietary restrictions, accessibility needs, etc."
                        rows={2}
                        value={requestValue}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Group notes card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Group Notes
            <span className="text-sm font-normal text-muted-foreground ml-2">
              optional
            </span>
          </CardTitle>
          <CardDescription>
            Anything the organizer should know about your group.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            className="resize-none"
            onChange={(e) => updateContact({ groupNotes: e.target.value })}
            placeholder="e.g. We'll arrive 15 minutes early for setup"
            rows={3}
            value={groupNotes}
          />
        </CardContent>
      </Card>

      {/* Price summary + continue */}
      <div className="space-y-4">
        <PriceSummary
          attendeeCount={confirmedCount + waitlistedCount}
          isCoveredByTotalAccess={isCoveredByTotalAccess}
          price={eventPrice}
          waitlistedCount={waitlistedCount}
        />
        <Button className="w-full" onClick={handleContinue} type="button">
          Continue to Payment
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}
